import { Cross1Icon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Text } from '@radix-ui/themes';
import { KeyboardEvent, useRef, useState } from 'react';

export interface DeliveryEmailInputProps {
    emails: string[];
    onEmailsChange: (emails: string[]) => void;
    placeholder: string;
    suggestedEmails?: string[];
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const DeliveryEmailInput = ({
    emails,
    onEmailsChange,
    placeholder,
    suggestedEmails = [],
}: DeliveryEmailInputProps) => {
    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const availableSuggestions = suggestedEmails
        .filter(
            (e) =>
                e.toLowerCase().includes(inputValue.toLowerCase()) &&
                !emails.includes(e),
        )
        .slice(0, 5);

    const showDropdown = isFocused && availableSuggestions.length > 0;

    const addEmails = (rawInput: string) => {
        const newEmails = rawInput
            .split(/[\s,;]+/)
            .map((e) => e.trim())
            .filter((e) => e.length > 0 && !emails.includes(e));

        if (newEmails.length > 0) {
            onEmailsChange([...emails, ...newEmails]);
        }

        setInputValue('');
        setHighlightedIndex(0);
    };

    const removeEmail = (emailToRemove: string) => {
        onEmailsChange(emails.filter((e) => e !== emailToRemove));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const submitEmail = () => {
            e.preventDefault();
            if (showDropdown && availableSuggestions[highlightedIndex]) {
                addEmails(availableSuggestions[highlightedIndex]);
            } else {
                addEmails(inputValue);
            }
        };

        const keyHandlers: Record<string, () => void> = {
            ArrowDown: () => {
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    Math.min(prev + 1, availableSuggestions.length - 1),
                );
            },
            ArrowUp: () => {
                e.preventDefault();
                setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            },
            Backspace: () => {
                if (inputValue === '' && emails.length > 0) {
                    onEmailsChange(emails.slice(0, -1));
                }
            },
            Enter: submitEmail,
            ' ': submitEmail,
            ',': submitEmail,
            ';': submitEmail,
        };

        const handler = keyHandlers[e.key];
        if (handler) {
            handler();
        }
    };

    return (
        <Flex
            wrap='wrap'
            gap='2'
            align='center'
            style={{
                minHeight: '40px',
                padding: '6px 12px',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: isFocused ? 'var(--accent-8)' : 'var(--gray-a7)',
                outline: isFocused ? '1px solid var(--accent-8)' : 'none',
                borderRadius: 'var(--radius-2)',
                backgroundColor: 'var(--color-surface)',
                position: 'relative',
            }}
            onClick={() => inputRef.current?.focus()}
        >
            {emails.map((email) => {
                const isValid = EMAIL_REGEX.test(email);

                return (
                    <Badge
                        key={email}
                        size='2'
                        color={isValid ? 'gray' : 'ruby'}
                        variant='surface'
                    >
                        <Flex align='center' gap='1'>
                            {email}
                            <Cross1Icon
                                style={{
                                    cursor: 'pointer',
                                    width: '12px',
                                    height: '12px',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeEmail(email);
                                }}
                            />
                        </Flex>
                    </Badge>
                );
            })}

            <input
                ref={inputRef}
                type='text'
                value={inputValue}
                onChange={(e) => {
                    setInputValue(e.target.value);
                    setHighlightedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                onPaste={(e) => {
                    e.preventDefault();
                    addEmails(e.clipboardData.getData('text'));
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                    setTimeout(() => {
                        setIsFocused(false);
                        addEmails(inputValue);
                    }, 150);
                }}
                placeholder={emails.length === 0 ? placeholder : ''}
                style={{
                    flex: 1,
                    minWidth: '150px',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: 'var(--gray-12)',
                    fontSize: 'var(--font-size-3)',
                    fontFamily: 'inherit',
                }}
            />

            {showDropdown && (
                <Box
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        backgroundColor: 'var(--color-background)',
                        border: '1px solid var(--gray-a6)',
                        borderRadius: 'var(--radius-3)',
                        boxShadow: 'var(--shadow-5)',
                        zIndex: 50,
                        overflow: 'hidden',
                    }}
                >
                    {availableSuggestions.map((suggestion, index) => (
                        <Box
                            key={suggestion}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                addEmails(suggestion);
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                backgroundColor:
                                    highlightedIndex === index
                                        ? 'var(--accent-a3)'
                                        : 'transparent',
                            }}
                        >
                            <Text size='2'>{suggestion}</Text>
                        </Box>
                    ))}
                </Box>
            )}
        </Flex>
    );
};
